import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { resolveMenuTheme, menuCssVars } from '../theme/menuTheme';
import { trackProductView, getVisitorId } from '../analytics/track';
import { restaurantApi, customerApi } from '../api/apiClient';
import { formatPrice } from '../utils/currency';

export default function ItemPage({ restaurant }) {
  const { id }     = useParams();   // this is MongoDB _id
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const { addItem, removeItem, getItemQuantity, getCartCount } = useCart();
  const { tu, translateProduct, translateCategory, showOriginalNames } = useLanguage();
  const { customer, saveCustomerProfile } = useAuth();
  const isFav = !!customer?.favorites?.includes(String(id));
  const toggleFav = async () => {
    if (!customer) return;
    try {
      const r = await customerApi.toggleFavorite(String(productId));
      saveCustomerProfile({ ...customer, favorites: r.data.favorites });
    } catch {}
  };
  const shareUrl = window.location.href;
  const doShare = (kind) => {
    const text = product ? `${product.name} — ${restaurant.name}` : restaurant.name;
    if (kind === 'wa') window.open(`https://wa.me/?text=${encodeURIComponent(text + '\n' + shareUrl)}`, '_blank');
    else if (kind === 'tg') window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`, '_blank');
    else navigator.clipboard?.writeText(shareUrl);
  };

  // Find the product across the main restaurant AND every branch — products are
  // separate subdocuments per branch with their own _ids, so a branch item won't
  // exist in restaurant.products. We search everywhere so the detail page works
  // regardless of which menu (main or branch) the customer came from.
  const matchId = (p) => (p._id || p.id) === id;
  let rawProduct = restaurant.products.find(matchId);
  let sourceBranch = null;
  if (!rawProduct) {
    for (const b of restaurant.branches || []) {
      const bp = (b.products || []).find(matchId);
      if (bp) { rawProduct = bp; sourceBranch = b; break; }
    }
  }
  // Keep branch context consistent: URL param wins, else the branch we found it in.
  const branchSlug = searchParams.get('branch') || sourceBranch?.slug || null;
  const menuUrl    = branchSlug ? `/menu?branch=${branchSlug}` : '/menu';
  const cartUrl    = branchSlug ? `/cart?branch=${branchSlug}` : '/cart';

  const product   = rawProduct ? translateProduct(rawProduct) : rawProduct;
  const productId = product?._id || product?.id;

  // On the combined "all branches" view (no specific branch), show which branches
  // carry this item when it isn't offered at every branch.
  const activeBranches = (restaurant.branches || []).filter(b => b.isActive);
  const aggregateEnabled = restaurant.aggregateMenu !== false && activeBranches.length > 0;
  const availableAt = (!branchSlug && aggregateEnabled && product)
    ? activeBranches.filter(b => (b.products || []).some(p => String(p.name || '').trim().toLowerCase() === String(rawProduct.name || '').trim().toLowerCase())).map(b => b.name)
    : [];
  const showBranchTag = availableAt.length > 0 && availableAt.length < activeBranches.length;
  const qty = getItemQuantity(restaurant.slug, productId);
  const [imgErr, setImgErr] = useState(false);

  // Menu theme palette (shared with the menu + cart pages)
  const { theme: menuTheme, colors: menuColors } = resolveMenuTheme(restaurant);

  // Track product detail views (one per product viewed)
  useEffect(() => {
    if (productId) trackProductView({ _id: productId, name: product?.name, price: product?.price, categorySlug: product?.categorySlug });
    // eslint-disable-next-line
  }, [productId]);

  // Per-product page title + meta description (browser tab, history, shares)
  useEffect(() => {
    if (!product) return;
    const prev = document.title;
    document.title = `${product.name} — ${restaurant.name}`;
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute('content');
    if (desc && product.description) desc.setAttribute('content', product.description.slice(0, 160));
    return () => { document.title = prev; if (desc && prevDesc != null) desc.setAttribute('content', prevDesc); };
    // eslint-disable-next-line
  }, [productId]);

  // Server-driven recommendations ("frequently bought together" / co-viewed)
  const [recs, setRecs] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!productId) return;
    setRecs(null);
    restaurantApi.getRecommendations(restaurant.slug, { productId, visitorId: getVisitorId(), limit: 4 })
      .then(r => { if (alive) setRecs(r.data || []); })
      .catch(() => { if (alive) setRecs([]); });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [productId]);

  if (!product) {
    return (
      <div style={{ minHeight: '100vh', background: '#0f0f0f', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', fontFamily: 'DM Sans, sans-serif' }}>
        <div style={{ fontSize: '4rem' }}>🍽️</div>
        <h2>Item not found</h2>
        <button onClick={() => navigate(menuUrl)} style={{ padding: '0.75rem 2rem', background: restaurant.primaryColor, border: 'none', borderRadius: '10px', color: '#fff', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '1rem' }}>{tu('cart_back')}</button>
      </div>
    );
  }

  // Recommendations from the server (co-purchase/co-view), translated for display.
  // Falls back to same-category items until recs load or if none are returned.
  const sameCategory = restaurant.products
    .filter(p => p.categorySlug === product.categorySlug && (p._id || p.id) !== productId)
    .slice(0, 4);
  const relatedItems = ((recs && recs.length ? recs : sameCategory)).map(translateProduct);

  const rawCategory = restaurant.categories.find(c => c.slug === product.categorySlug);
  const category    = rawCategory ? translateCategory(rawCategory) : rawCategory;

  const handleAdd    = () => addItem(restaurant.slug, { ...product, id: productId });
  const handleRemove = () => removeItem(restaurant.slug, productId);

  return (
    <div className={`item-page theme-${menuTheme}`} style={menuCssVars(menuColors)}>
      <div className="item-hero">
        {!imgErr
          ? <img src={product.image} alt={product.name} className="item-hero-img" onError={() => setImgErr(true)} />
          : <div className="item-hero-fallback">🍽️</div>
        }
        <div className="item-hero-overlay" />
        <button className="item-back-btn" onClick={() => navigate(menuUrl)}>{tu('cart_back')}</button>
      </div>

      <div className="item-content">
        <div className="item-main">
          <div className="item-breadcrumb">
            {category?.icon} {category?.name}
            {product.subCategory && <> · {product.subCategory}</>}
          </div>
          <div className="item-title-row">
            <h1 className="item-title">{product.name}</h1>
            {showOriginalNames && product._orig?.name && product._orig.name !== product.name && (
              <span className="item-title-orig" dir="auto">{tu('menu_original_name')}: {product._orig.name}</span>
            )}
            {customer && (
              <button className={`item-fav ${isFav ? 'on' : ''}`} onClick={toggleFav}
                aria-label={isFav ? tu('fav_remove') : tu('fav_add')}>{isFav ? '♥' : '♡'}</button>
            )}
          </div>
          <div className="item-tags">
            {product.tags?.map(tag => <span key={tag} className="item-tag">{tag}</span>)}
            {!product.available && <span className="item-tag unavail-tag">{tu('menu_unavailable')}</span>}
          </div>
          {showBranchTag && (
            <div className="item-branch-tag">📍 {tu('menu_only_at')}: {availableAt.join('، ')}</div>
          )}
          <div className="item-share-row">
            <button className="item-share-btn" onClick={() => doShare('wa')}>💬 {tu('share_whatsapp')}</button>
            <button className="item-share-btn" onClick={() => doShare('tg')}>✈️ {tu('share_telegram')}</button>
            <button className="item-share-btn" onClick={() => doShare('copy')}>🔗 {tu('share_copy')}</button>
          </div>
          <p className="item-description">{product.description}</p>
          {product.options?.length > 0 && (
            <div className="item-options">
              {product.options.map((o, i) => (
                <span key={i} className="item-option" title={o.label}>
                  {o.icon ? <span className="item-option-icon">{o.icon}</span> : null}
                  <span>{o.label}</span>
                </span>
              ))}
            </div>
          )}
          <div className="item-action-bar">
            <div className="item-price">{formatPrice(product.price, restaurant.currencyCode)}</div>
            {product.available && (
              <div className="item-qty-control">
                {qty > 0 ? (
                  <>
                    <button className="item-qty-btn" onClick={handleRemove}>−</button>
                    <span className="item-qty-num">{qty}</span>
                    <button className="item-qty-btn item-qty-add" onClick={handleAdd}>+</button>
                  </>
                ) : (
                  <button className="item-add-btn" onClick={handleAdd}>{tu('menu_add')}</button>
                )}
              </div>
            )}
          </div>
        </div>

        {relatedItems.length > 0 && (
          <div className="related-section">
            <h3 className="related-title">{tu('menu_related') || 'You might also like'}</h3>
            <div className="related-grid">
              {relatedItems.map(item => {
                const rid = item._id || item.id;
                return (
                  <div key={rid} className="related-card" onClick={() => navigate(`/item/${rid}${branchSlug ? `?branch=${branchSlug}` : ''}`)}>
                    <img src={item.image} alt={item.name} className="related-img" onError={e => e.target.style.display='none'} />
                    <div className="related-info">
                      <div className="related-name">{item.name}</div>
                      <div className="related-price">{formatPrice(item.price, restaurant.currencyCode)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Floating cart — same as the menu page, so customers can checkout from here */}
      {getCartCount(restaurant.slug) > 0 && (
        <div className="item-floating-cart" onClick={() => navigate(cartUrl)}>
          <span>{getCartCount(restaurant.slug)} {tu('menu_items')}</span>
          <span>{tu('menu_view_cart')}</span>
        </div>
      )}

      <style>{`
        .item-floating-cart { position: fixed; bottom: 1.5rem; left: 50%; transform: translateX(-50%); display: flex; align-items: center; gap: 1.25rem; background: var(--accent); color: var(--accent-text); padding: .9rem 1.6rem; border-radius: 999px; cursor: pointer; font-family: 'DM Sans', sans-serif; font-weight: 700; font-size: .92rem; box-shadow: 0 10px 34px var(--shadow); z-index: 90; animation: itemCartIn .3s cubic-bezier(.2,.7,.3,1); white-space: nowrap; max-width: calc(100vw - 2rem); }
        .item-floating-cart:hover { opacity: .92; }
        @keyframes itemCartIn { from { transform: translate(-50%, 20px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }
        .item-page { min-height: 100vh; background: var(--bg); color: var(--text); font-family: 'DM Sans', sans-serif; transition: background .3s, color .3s; }
        .item-hero { position: relative; height: 50vh; min-height: 300px; overflow: hidden; }
        .item-hero-img { width: 100%; height: 100%; object-fit: cover; }
        .item-hero-fallback { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 5rem; background: var(--surface-2); }
        .item-hero-overlay { position: absolute; inset: 0; background: linear-gradient(to top, var(--bg) 0%, transparent 60%); }
        .item-back-btn { position: absolute; top: 1.5rem; left: 1.5rem; background: rgba(0,0,0,0.5); backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.15); color: #fff; border-radius: 8px; padding: 0.6rem 1.2rem; cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: 0.9rem; transition: background 0.2s; }
        .item-back-btn:hover { background: rgba(0,0,0,0.8); }
        .item-content { max-width: 800px; margin: 0 auto; padding: 2rem; }
        .item-main { background: var(--surface); border: 1px solid var(--border); border-radius: 20px; padding: 2.5rem; margin-top: -4rem; position: relative; z-index: 2; margin-bottom: 2rem; box-shadow: 0 12px 40px var(--shadow); }
        .item-breadcrumb { color: var(--faint); font-size: 0.85rem; margin-bottom: 1rem; }
        .item-title { font-family: 'Playfair Display', serif; font-size: 2.5rem; font-weight: 900; color: var(--text); margin-bottom: 1rem; line-height: 1.1; }
        .item-title-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
        .item-title-orig { display: block; width: 100%; font-size: .9rem; font-weight: 600; color: var(--muted); opacity: .8; margin: -.5rem 0 1rem; }
        .item-branch-tag { display: inline-block; font-size: .82rem; font-weight: 700; color: var(--primary,#c8a96e); background: color-mix(in srgb, var(--primary,#c8a96e) 14%, transparent); border: 1px solid color-mix(in srgb, var(--primary,#c8a96e) 30%, transparent); padding: .35rem .75rem; border-radius: 8px; margin: 0 0 1rem; }
        .item-fav { flex-shrink: 0; width: 46px; height: 46px; border-radius: 50%; border: 1px solid var(--border); background: var(--surface-2); color: var(--muted); font-size: 1.3rem; cursor: pointer; transition: all .2s; }
        .item-fav.on { color: #f87171; border-color: rgba(248,113,113,.4); }
        .item-fav:hover { transform: scale(1.08); }
        .item-share-row { display: flex; flex-wrap: wrap; gap: .5rem; margin-bottom: 1.25rem; }
        .item-share-btn { padding: .45rem .9rem; border-radius: 20px; border: 1px solid var(--border); background: var(--surface-2); color: var(--muted); font-family: 'DM Sans', sans-serif; font-size: .78rem; cursor: pointer; transition: all .2s; }
        .item-share-btn:hover { color: var(--text); border-color: var(--accent); }
        .item-tags { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 1.5rem; }
        .item-tag { padding: 0.3rem 0.9rem; border-radius: 20px; background: var(--surface-2); color: var(--muted); font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
        .unavail-tag { background: rgba(239,68,68,0.15); color: #f87171; }
        .item-description { color: var(--muted); line-height: 1.8; font-size: 1.05rem; margin-bottom: 2rem; }
        .item-options { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 2rem; }
        .item-option { display: inline-flex; align-items: center; gap: 0.35rem; padding: 0.4rem 0.9rem; border-radius: 20px; background: color-mix(in srgb, var(--accent) 12%, transparent); border: 1px solid color-mix(in srgb, var(--accent) 30%, transparent); color: var(--accent); font-size: 0.85rem; font-weight: 600; }
        .item-option-icon { font-size: 1rem; }
        .item-action-bar { display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; padding-top: 1.5rem; border-top: 1px solid var(--border); }
        .item-price { font-size: 2rem; font-weight: 700; color: var(--accent); min-width: 0; overflow-wrap: anywhere; }
        .item-qty-control { flex-shrink: 0; }
        .item-qty-control { display: flex; align-items: center; gap: 1rem; }
        .item-qty-btn { width: 44px; height: 44px; border-radius: 50%; border: 2px solid var(--border); background: var(--surface-2); color: var(--text); font-size: 1.3rem; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
        .item-qty-btn:hover { background: var(--accent-soft); }
        .item-qty-add { background: var(--accent); border-color: var(--accent); color: var(--accent-text); }
        .item-qty-add:hover { opacity: 0.85; }
        .item-qty-num { font-size: 1.5rem; font-weight: 700; color: var(--text); min-width: 40px; text-align: center; }
        .item-add-btn { padding: 0.9rem 2.5rem; border-radius: 12px; background: var(--accent); border: none; color: var(--accent-text); font-family: 'DM Sans', sans-serif; font-size: 1.05rem; font-weight: 700; cursor: pointer; transition: opacity 0.2s, transform 0.2s; white-space: nowrap; }
        .item-add-btn:hover { opacity: 0.85; transform: scale(1.02); }
        /* Currency-overflow safety: big Rial/Toman amounts shrink instead of
           overflowing the action bar; the price uses tidy tabular figures. */
        .item-price { font-size: clamp(1.4rem, 5vw, 2rem); font-variant-numeric: tabular-nums; }
        .related-price { font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
        .related-section { margin-top: 1rem; }
        .related-title { font-family: 'Playfair Display', serif; font-size: 1.3rem; color: var(--text); margin-bottom: 1rem; }
        .related-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 1rem; }
        .related-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; cursor: pointer; transition: transform 0.2s, box-shadow .2s; }
        .related-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px var(--shadow); }
        .related-img { width: 100%; height: 110px; object-fit: cover; }
        .related-info { padding: 0.75rem; }
        .related-name { font-size: 0.85rem; font-weight: 600; color: var(--text); margin-bottom: 0.25rem; }
        .related-price { font-size: 0.85rem; color: var(--accent); font-weight: 700; }
      `}</style>
    </div>
  );
}
